import React, { useEffect, useState, useMemo, useCallback } from 'react';
import ProductCard from '../ProductCard/ProductCard';
import { t } from '../../i18n';
import RecommendationService from '../../services/recommendations';

const scrollRowClass =
  'flex gap-6 overflow-x-auto pb-2 scrollbar-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

function CardSkeleton() {
  return (
    <div className="min-w-[280px] md:min-w-[320px]">
      <div className="mb-2 h-[200px] w-full animate-pulse rounded-2xl bg-slate-200" />
      <div className="mb-2 h-6 w-[80%] animate-pulse rounded bg-slate-200" />
      <div className="mb-2 h-5 w-[60%] animate-pulse rounded bg-slate-200" />
      <div className="h-5 w-[40%] animate-pulse rounded bg-slate-200" />
    </div>
  );
}

const SmartRecommendations = ({ productId, currentProduct }) => {
  const [recommendations, setRecommendations] = useState({
    related: [],
    best_deals: [],
    personalized: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trendingFallback, setTrendingFallback] = useState([]);

  const fetchRecommendations = useCallback(async () => {
    if (!productId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await RecommendationService.getProductRecommendations(productId);
      if (result.success) {
        const recs = {
          related: result.related || [],
          best_deals: result.best_deals || [],
          personalized: result.personalized || []
        };
        setRecommendations(recs);

        // If ALL sections are empty, fetch trending as fallback
        const hasAny = recs.related.length > 0 || recs.best_deals.length > 0 || recs.personalized.length > 0;
        if (!hasAny) {
          try {
            const trending = await RecommendationService.getTrendingProducts(8);
            setTrendingFallback(trending.products || []);
          } catch (_) {}
        }
      } else {
        setError(t('recommendations.loadFailedShort'));
        setRecommendations({ related: [], best_deals: [], personalized: [] });
        // Still try trending fallback
        try {
          const trending = await RecommendationService.getTrendingProducts(8);
          setTrendingFallback(trending.products || []);
        } catch (_) {}
      }
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setError(t('recommendations.loadFailedShort'));
      setRecommendations({ related: [], best_deals: [], personalized: [] });
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  // Memoized sections data
  const sections = useMemo(() => [
    {
      key: 'personalized',
      title: `🎯 ${t('recommendations.personalTitle')}`,
      subtitle: t('recommendations.personalSubtitle'),
      products: recommendations.personalized,
      show: recommendations.personalized.length > 0
    },
    {
      key: 'related',
      title: `🔥 ${t('recommendations.relatedTitle')}`,
      subtitle: t('recommendations.relatedSubtitle'),
      products: recommendations.related,
      show: recommendations.related.length > 0
    },
    {
      key: 'best_deals',
      title: `💸 ${t('recommendations.dealsTitle')}`,
      subtitle: t('recommendations.dealsSubtitle'),
      products: recommendations.best_deals,
      show: recommendations.best_deals.length > 0
    }
  ], [recommendations]);

  // If no recommendations AND not loading, show trending fallback
  if (!loading && sections.every(section => !section.show)) {
    if (trendingFallback.length > 0) {
      return (
        <div className="mb-4 mt-8">
          <div className="mb-4">
            <h2 className="mb-1 text-2xl font-bold text-slate-900 sm:text-3xl md:text-4xl">
              🔥 {t('recommendations.trendingTitle')}
            </h2>
            <p className="text-base text-slate-600 sm:text-lg">
              {t('recommendations.trendingSubtitle')}
            </p>
          </div>
          <div className={scrollRowClass}>
            {trendingFallback.map((product) => (
              <div key={product.id} className="min-w-[280px] md:min-w-[320px]">
                <ProductCard data={product} />
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null; // Nothing to show — hide the section entirely (cleaner than a message)
  }

  return (
    <div className="mb-4 mt-8">
      {sections.map((section) => {
        if (!section.show && !loading) return null;

        return (
          <div key={section.key} className="mb-12">
            {/* Section Header */}
            <div className="mb-4">
              <h2 className="mb-1 flex items-center gap-2 text-2xl font-bold text-slate-900 sm:text-3xl md:text-4xl">
                {section.title}
              </h2>
              <p className="text-sm text-slate-600 sm:text-base">
                {section.subtitle}
              </p>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className={scrollRowClass}>
                {Array.from({ length: 4 }).map((_, index) => (
                  <CardSkeleton key={index} />
                ))}
              </div>
            ) : (
              <div className={scrollRowClass}>
                {section.products.map((product) => (
                  <div key={product.id} className="min-w-[280px] md:min-w-[320px]">
                    <ProductCard data={product} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Error Message */}
      {error && !loading && (
        <div className="py-8 text-center">
          <p className="text-slate-600">
            {error}
          </p>
          <div className="mt-4">
            <button
              type="button"
              onClick={fetchRecommendations}
              className="rounded-lg bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-indigo-700"
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(SmartRecommendations);
